import { useEffect, useState ,Suspense  } from "react";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  useMediaQuery,
  Fade,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { getTheme } from "./theme";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { APP_BAR_HEIGHT } from "./components/MyAppBar/MyAppBar"; // <— add this import

import generateFirebaseToken from "./notifications/firebase-token-generate";

import Home from "./components/Home/Home";
import MyAppBar from "./components/MyAppBar/MyAppBar";
import SideBar from "./components/SideBar/SideBar";
import SignUp from "./components/SignUp/SignUp";
import SignIn from "./components/SignIn/SignIn";
import NewTask from "./components/Task/newTask";
import TaskDetails from "./components/Task/TaskDetails";
import EditTask from "./components/Task/EditTask";
import { onMessage } from "firebase/messaging";
import { toast, ToastContainer } from "react-toastify";
import { messaging } from "./notifications/firebase";
import "react-toastify/dist/ReactToastify.css";

import Notifications from "./components/Notification/Notifications";
import Profile from "./components/Profile/Profile";
import Projects from "./components/Projects/Projects";
import ReviewRequests from "./components/ReviewRequests/ReviewRequests";
import axios from "./api/axiosConfig"; // Use configured axios with retry logic
import { baseUrl } from "./constants";
import noveraLogo from "./assets/novera2.png";
import SettingsPage from "./components/settings/settings";
import ChangePassword from "./components/ChangePassword/ChangePassword";
import About from "./components/About/About";
import Users from "./components/Users/Users";
import DashboardPage from "./components/Dashboard/DashboardPage";
import Workspace from "./components/Workspace/WorkspaceManager";
import NoteBook from "./components/NoteBook/NoteBook";
import Account from "./components/Account/Accounts";
import AccountDetails from "./components/Account/AccountDetails";
import ChatPage from "./components/chat/ChatPage";
import ProjectDetails from "./components/Projects/ProjectsDetails";
import Departments from "./components/Departments/Departments";
import DepartmentDetails from "./components/Departments/DepartmentDetails";
import ApprovalsAdminPage from "./components/approvals/ApprovalsAdminPage";
import Branches from "./components/Branches/Branches";
import AssignmentSettingsPage from "./components/AssignmentSettings/AssignmentSettingsPage";

import { renderHRRoutes } from "./routes/hrRoutes";
import { renderCRMRoutes } from "./routes/crmRoutes";
import { renderInventoryRoutes } from "./routes/inventoryRoutes";
import { renderPurchasingRoutes } from "./routes/purchasingRoutes";
import { renderInvoicingRoutes } from "./routes/invoicingRoutes";

/* ------------------------------------------------------------------
   Chat socket context + provider
------------------------------------------------------------------- */
import { ChatSocketProvider } from "./components/chat/ChatSocketProvider";

/* ------------------------------------------------------------------
   Helpers
------------------------------------------------------------------- */
function getStoredToken() {
  return (
    localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken")
  );
}

function setStoredToken(token: string, storage: "local" | "session") {
  if (storage === "local") {
    localStorage.setItem("accessToken", token);
  } else {
    sessionStorage.setItem("accessToken", token);
  }
}

// Keep this function for future use (logout, etc.)
// function clearStoredTokens() {
//   localStorage.removeItem("accessToken");
//   sessionStorage.removeItem("accessToken");
// }

/* ------------------------------------------------------------------
   Route guards (React Router v6)
------------------------------------------------------------------- */

function ProtectedRoute({
  isAuthenticated,
  children,
}: {
  isAuthenticated: boolean;
  children: JSX.Element;
}) {
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  return children;
}

function GuestRoute({
  isAuthenticated,
  children,
}: {
  isAuthenticated: boolean;
  children: JSX.Element;
}) {
  // If already authed, don't show SignIn/SignUp; send home
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  return children;
}

/* ------------------------------------------------------------------
   App root
------------------------------------------------------------------- */

function App() {
  const [mode, setMode] = useState<"light" | "dark">("light");

  useEffect(() => {
    const saved = localStorage.getItem("themeMode") as "light" | "dark" | null;
    if (saved) setMode(saved);
  }, []);

  // Set document title to ensure it's updated
  useEffect(() => {
    document.title = "NOVERA ERP SYSTEM";
  }, []);

  const toggleTheme = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem("themeMode", newMode);
  };

  return (
    <ThemeProvider theme={getTheme(mode)}>
      <CssBaseline />
      <ChatSocketProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AppContent toggleTheme={toggleTheme} />
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            pauseOnHover={false}
            draggable={false}
            limit={3}
          />
        </Router>
      </ChatSocketProvider>
    </ThemeProvider>
  );
}

/* ------------------------------------------------------------------
   App content (layout + routes)
------------------------------------------------------------------- */

function AppContent({ toggleTheme }: { toggleTheme: () => void }) {
  const drawerWidth = 240;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true); // Persistent sidebar state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(true);

  // Try refresh on load using whichever storage had the token
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout | null = null;
    const loadingCompletedRef = { current: false };

            const refreshToken = async () => {
              const local = localStorage.getItem("accessToken");
              const session = sessionStorage.getItem("accessToken");
              const hadStorage: "local" | "session" | null = local
                ? "local"
                : session
                ? "session"
                : null;
              const oldAccessToken = local || session;

              // If no token, immediately show login
              if (!oldAccessToken) {
                setIsAuthenticated(false);
                setLoading(false);
                loadingCompletedRef.current = true;
                if (timeoutId) clearTimeout(timeoutId);
                return;
              }

              // If we have a token, user is authenticated immediately
              setIsAuthenticated(true);
              setLoading(false);
              loadingCompletedRef.current = true;
              if (timeoutId) clearTimeout(timeoutId);

      // Try to refresh the token in background (don't block UI)
      // Axios now handles retries automatically via interceptor
      try {
        const response = await axios.post(`${baseUrl}/auth/refresh`, {
          token: oldAccessToken,
        }, { 
          timeout: 15000,
          validateStatus: (status) => status >= 200 && status < 500
        });

        const { access_token } = response.data || {};
        if (access_token && mounted) {
          // Update token if refresh succeeded
          setStoredToken(access_token, hadStorage!);
          window.dispatchEvent(new Event("auth-token-changed"));
        }
      } catch (error) {
        // Silently ignore errors - axios retry already handled it
        // Token might still be valid, user can continue using the app
      }

      // Initialize Firebase (non-blocking)
      generateFirebaseToken().catch((firebaseError) => {
        console.error("Firebase token generation failed (non-critical):", firebaseError);
      });
    };

    // Set safety timeout BEFORE executing refresh
    timeoutId = setTimeout(() => {
      if (mounted && !loadingCompletedRef.current) {
        console.warn("Loading timeout (8s), forcing app to load");
        setLoading(false);
      }
    }, 8000);

    // Execute token refresh (will clear timeout if successful)
    refreshToken();

    // Cosmetic splash fade
    const fadeTimeout = setTimeout(() => setFadeIn(false), 3000);

            // React to token changes from other parts of app (SignIn/SignOut)
            const onChange = () => {
              const exists = !!getStoredToken();
              setIsAuthenticated(exists);
              setLoading(false);
              loadingCompletedRef.current = true;
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
            };
    window.addEventListener("auth-token-changed", onChange);

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      clearTimeout(fadeTimeout);
      window.removeEventListener("auth-token-changed", onChange);
    };
  }, []);

  // Foreground Firebase message listener
  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      const title = payload.data?.title ?? "🔔 New Notification";
      const body = payload.data?.body ?? "You have a new update.";

      toast.info(
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ fontSize: "20px", marginRight: "10px" }}>📬</div>
          <div>
            <strong style={{ fontSize: "15px" }}>{title}</strong>
            <div style={{ fontSize: "13px", color: "#555", marginTop: "4px" }}>
              {body}
            </div>
          </div>
        </div>,
        {
          toastId: `notif-${Date.now()}`,
          autoClose: 5000,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          closeButton: true,
          hideProgressBar: false,
          icon: false,
          style: {
            background: "#fff",
            color: "#333",
            borderLeft: "5px solid #5DB0FF",
            borderRadius: "8px",
            padding: "12px 16px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            maxWidth: "350px",
          },
          progressClassName: "custom-toast-progress",
        }
      );

      window.dispatchEvent(new CustomEvent("refresh-unseen-notifications"));
    });

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

          const handleDrawerToggle = () => setMobileOpen((v) => !v);
          const handleSidebarToggle = () => setSidebarOpen((v) => !v);

          if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Fade in={fadeIn} timeout={1000}>
          <Box
            component="img"
            src={noveraLogo}
            alt="Novera ERP System"
            sx={{
              maxWidth: "80%",
              maxHeight: "80%",
              objectFit: "contain",
              filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.3))",
              animation: "pulse 2s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": {
                  transform: "scale(1)",
                  opacity: 1,
                },
                "50%": {
                  transform: "scale(1.05)",
                  opacity: 0.9,
                },
              },
            }}
          />
        </Fade>
        
        {/* Loading indicator */}
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "white",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              border: "2px solid rgba(255,255,255,0.3)",
              borderTop: "2px solid white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          Loading Novera ERP System...
        </Box>
      </Box>
    );
  }

  function RouteLoader() {
  return <Box sx={{ flex: 1, p: 2 }}>Loading…</Box>;
}

// after
const Protected = ({ children }: { children: JSX.Element }) => (
  <ProtectedRoute isAuthenticated={isAuthenticated}>
    <Suspense fallback={<RouteLoader />}>{children}</Suspense>
  </ProtectedRoute>
);

          return (
            <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
              {/* App shell only when authenticated */}
              {isAuthenticated && (
                <>
                  {/* Fixed App Bar */}
                  <MyAppBar
                    isMobile={isMobile}
                    drawerWidth={sidebarOpen ? drawerWidth : 64}
                    handleDrawerToggle={handleDrawerToggle}
                    sx={{
                      position: "fixed !important",
                      top: 0,
                      left: `${sidebarOpen ? drawerWidth : 64}px !important`,
                      right: 0,
                      zIndex: 1200,
                      transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      width: `calc(100% - ${sidebarOpen ? drawerWidth : 64}px) !important`,
                      marginLeft: "0 !important",
                    }}
                  />

                  {/* Fixed Sidebar */}
                  <SideBar
                    isMobile={isMobile}
                    drawerWidth={drawerWidth}
                    mobileOpen={mobileOpen}
                    sidebarOpen={sidebarOpen}
                    handleDrawerToggle={handleDrawerToggle}
                    handleSidebarToggle={handleSidebarToggle}
                    sx={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      height: "100vh",
                      zIndex: 1100,
                    }}
                  />
                </>
              )}

           {/* Main content area with proper positioning */}
            <Box
              component="main"
              sx={{
                position: "fixed",
                top: isAuthenticated ? `${APP_BAR_HEIGHT}px` : 0,
                left: isAuthenticated ? (sidebarOpen ? `${drawerWidth}px` : "64px") : 0,
                right: 0,
                bottom: 0,
                height: isAuthenticated ? `calc(100vh - ${APP_BAR_HEIGHT}px)` : "100vh",
                overflow: "auto",
                px: { xs: 2, sm: 3 },
                pb: 3,
                boxSizing: "border-box",
                transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                zIndex: 1,
              }}
            >

              <Routes>
        {/* Guest pages */}
        <Route
          path="/"
          element={
            <GuestRoute isAuthenticated={isAuthenticated}>
              <SignIn />
            </GuestRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <GuestRoute isAuthenticated={isAuthenticated}>
              <SignUp />
            </GuestRoute>
          }
        />

        {/* Protected pages */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Projects />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-task/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <NewTask />
            </ProtectedRoute>
          }
        />
        <Route
          path="/task/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <TaskDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-task/:taskId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <EditTask />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/review-requests"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ReviewRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <SettingsPage toggleTheme={toggleTheme} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/branches"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Branches />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ChangePassword />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/about"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <About />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Workspace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notebook"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <NoteBook />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AccountDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:channelId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ProjectDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/departments"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Departments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/departments/:departmentId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <DepartmentDetails />
            </ProtectedRoute>
          }
        />

        <Route
          path="/approvals"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ApprovalsAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignment-settings"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AssignmentSettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback: anything unknown -> if authed go /home else / */}
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {renderHRRoutes(Protected)}
        {renderCRMRoutes(Protected)}
        {renderInventoryRoutes(Protected)}
        {renderPurchasingRoutes(Protected)}
        {renderInvoicingRoutes(Protected)}

        {/* Fallback */}
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>
    </Box>
     </Box>
  );
}

export default App;
